import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Grid, Typography, Button, Tabs, Tab, TextField, MenuItem,
  TablePagination, Switch, Tooltip,
} from "@mui/material";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import moment from "moment-timezone";

import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import { endPoints } from "../../constant/Environment";
import { getAllData, addData, updateData } from "../../Utility/API";
import { tokens } from "../../theme";
import { mono, pill, th, td, filterFieldSx, tabsSx, ConfirmDialog, TablePaginationActions } from "../../components/tableKit";

const fmtDateTime = (d) => (d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm") : "—");

// Matches RiskLevel in the API: LOW 0-30, MEDIUM 31-60, HIGH 61-80, CRITICAL 81+.
const levelKind = (level) => {
  switch (String(level || "").toUpperCase()) {
    case "CRITICAL": return "bad";
    case "HIGH": return "bad";
    case "MEDIUM": return "warn";
    default: return "neutral";
  }
};

const reviewKind = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "BLOCKED": return "bad";
    case "PENDING":
    case "IN_REVIEW": return "warn";
    case "CLEARED": return "ok";
    default: return "neutral";
  }
};

const decisionKind = (decision) => {
  switch (String(decision || "").toUpperCase()) {
    case "BLOCK": return "bad";
    case "REVIEW": return "warn";
    case "MONITOR": return "neutral";
    default: return "ok";
  }
};

const LEVEL_OPTIONS = [
  { value: "", label: "All levels" },
  { value: "CRITICAL", label: "Critical" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

const REVIEW_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_REVIEW", label: "In review" },
  { value: "CLEARED", label: "Cleared" },
  { value: "BLOCKED", label: "Blocked" },
  { value: "NONE", label: "None" },
];

const DECISION_OPTIONS = [
  { value: "", label: "All decisions" },
  { value: "BLOCK", label: "Blocked" },
  { value: "REVIEW", label: "Review" },
  { value: "MONITOR", label: "Monitor" },
];

const ACTION_OPTIONS = ["BLOCK", "REVIEW", "MONITOR", "ALLOW"];

export default function Risk() {
  const [tab, setTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [overview, setOverview] = useState({});

  // players tab
  const [players, setPlayers] = useState([]);
  const [playerTotal, setPlayerTotal] = useState(0);
  const [levelFilter, setLevelFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [playerPage, setPlayerPage] = useState(0);
  const [playerRows, setPlayerRows] = useState(25);

  // events tab
  const [events, setEvents] = useState([]);
  const [eventTotal, setEventTotal] = useState(0);
  const [decisionFilter, setDecisionFilter] = useState("");
  const [eventPage, setEventPage] = useState(0);
  const [eventRows, setEventRows] = useState(25);

  // rules tab
  const [rules, setRules] = useState([]);

  const [confirm, setConfirm] = useState(null);

  const loadOverview = useCallback(async () => {
    const response = await getAllData(endPoints.api.RISK_OVERVIEW);
    if (response?.status === "success") setOverview(response.data || {});
  }, []);

  const loadPlayers = useCallback(async () => {
    const params = new URLSearchParams();
    if (levelFilter) params.append("risk_level", levelFilter);
    if (reviewFilter) params.append("review_status", reviewFilter);
    params.append("page", playerPage + 1);
    params.append("count", playerRows);
    const response = await getAllData(`${endPoints.api.RISK_PLAYERS}?${params.toString()}`);
    if (response?.status === "success") {
      // rows and total are nested inside data: the API rewrites every response as
      // status/message/data, so a sibling total key would not survive the trip.
      const rows = response.data?.rows || [];
      setPlayers(rows);
      setPlayerTotal(response.data?.total ?? rows.length);
    }
  }, [levelFilter, reviewFilter, playerPage, playerRows]);

  const loadEvents = useCallback(async () => {
    const params = new URLSearchParams();
    if (decisionFilter) params.append("decision", decisionFilter);
    params.append("page", eventPage + 1);
    params.append("count", eventRows);
    const response = await getAllData(`${endPoints.api.RISK_EVENTS}?${params.toString()}`);
    if (response?.status === "success") {
      const rows = response.data?.rows || [];
      setEvents(rows);
      setEventTotal(response.data?.total ?? rows.length);
    }
  }, [decisionFilter, eventPage, eventRows]);

  const loadRules = useCallback(async () => {
    const response = await getAllData(endPoints.api.RISK_RULES);
    if (response?.status === "success") {
      const sorted = [...(response.data || [])].sort(
        (a, b) => String(a.rule_type).localeCompare(String(b.rule_type)) ||
          String(a.rule_code).localeCompare(String(b.rule_code))
      );
      setRules(sorted);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([loadOverview(), loadPlayers(), loadEvents(), loadRules()])
      .catch((e) => console.error(e))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadPlayers(); }, [loadPlayers]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const submitReview = async (player, reviewStatus, blocked, note) => {
    const suffix = blocked === null ? "/review" : "/block";
    const body = blocked === null ? { review_status: reviewStatus, note } : { blocked, note };
    const response = await addData(`${endPoints.api.RISK_PLAYERS}/${player.id_user}${suffix}`, body);
    if (response?.data?.status === "success" || response?.status === "success") {
      SuccesToast(response?.data?.message || response?.message || "Saved");
      await Promise.all([loadPlayers(), loadOverview()]);
    } else {
      ErrorToast(response?.data?.message || response?.message || "Could not save the review");
    }
    setConfirm(null);
  };

  const toggleRule = async (rule, enabled) => {
    const response = await updateData(endPoints.api.RISK_RULES, { rule_code: rule.rule_code, enabled });
    if (response?.data?.status === "success") {
      SuccesToast(`${rule.rule_code} ${enabled ? "enabled" : "disabled"}`);
      loadRules();
    } else {
      ErrorToast(response?.data?.message || "Could not update the rule");
    }
  };

  const changeRuleAction = async (rule, action) => {
    const response = await updateData(endPoints.api.RISK_RULES, {
      rule_code: rule.rule_code, action, threshold: rule.threshold,
    });
    if (response?.data?.status === "success") {
      SuccesToast(`${rule.rule_code} set to ${action}`);
      loadRules();
    } else {
      ErrorToast(response?.data?.message || "Could not update the rule");
    }
  };

  const changeRuleThreshold = async (rule, threshold) => {
    const parsed = threshold === "" ? null : Number(threshold);
    if (parsed !== null && Number.isNaN(parsed)) {
      ErrorToast("Threshold must be a number");
      return;
    }
    const response = await updateData(endPoints.api.RISK_RULES, {
      rule_code: rule.rule_code, threshold: parsed, action: rule.action,
    });
    if (response?.data?.status === "success") {
      SuccesToast(`${rule.rule_code} threshold updated`);
      loadRules();
    } else {
      ErrorToast(response?.data?.message || "Could not update the rule");
    }
  };

  if (isLoading) return <Loader />;

  return (
    <Box>
      <Toaster />
      <PageHeader
        title="Risk & Fraud"
        crumb="Operate / Risk & Fraud"
        subtitle="Responsible-gaming enforcement, risk scoring and manual review"
      />

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid item xs={6} md={2}>
          <MiniStat label="Critical players" value={overview.critical_risk ?? 0} color={tokens.danger} note="Score 81+" />
        </Grid>
        <Grid item xs={6} md={2}>
          <MiniStat label="High risk" value={overview.high_risk ?? 0} color={tokens.amber} note="Score 61-80" />
        </Grid>
        <Grid item xs={6} md={2}>
          <MiniStat label="Awaiting review" value={overview.manual_reviews ?? 0} color={tokens.amber} note="Pending / in review" />
        </Grid>
        <Grid item xs={6} md={2}>
          <MiniStat label="Blocked bets" value={overview.blocked_bets_24h ?? 0} color={tokens.danger} note="Last 24 hours" />
        </Grid>
        <Grid item xs={6} md={2}>
          <MiniStat label="Self-exclusions" value={overview.self_exclusions ?? 0} note="Active" />
        </Grid>
        <Grid item xs={6} md={2}>
          <MiniStat label="Cooling off" value={overview.cooling_off ?? 0} note="Active periods" />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabsSx}>
        <Tab label="Players" />
        <Tab label="Risk events" />
        <Tab label="Rules" />
      </Tabs>

      <Box sx={{ mt: 1.75 }}>
        {tab === 0 && (
          <SectionCard
            title="Players by risk score"
            subtitle="Highest score first. Blocking is a manual decision — the engine scores and queues, it never blocks on its own."
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField select size="small" label="Risk level" value={levelFilter} sx={filterFieldSx}
                  onChange={(e) => { setLevelFilter(e.target.value); setPlayerPage(0); }}>
                  {LEVEL_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Review status" value={reviewFilter} sx={filterFieldSx}
                  onChange={(e) => { setReviewFilter(e.target.value); setPlayerPage(0); }}>
                  {REVIEW_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
            }
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Player</th>
                    <th style={th}>Score</th>
                    <th style={th}>Level</th>
                    <th style={th}>Review</th>
                    <th style={th}>Last signal</th>
                    <th style={th}>When</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {players.length === 0 && (
                    <tr><td style={{ ...td, color: tokens.textDim }} colSpan={7}>No players have triggered a risk rule.</td></tr>
                  )}
                  {players.map((p) => (
                    <tr key={p.id_user}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{p.username || `#${p.id_user}`}</div>
                        <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>
                          {p.id_user}{p.self_exclude ? " · self-excluded" : ""}
                        </div>
                      </td>
                      <td style={{ ...td, fontFamily: mono, fontWeight: 700 }}>{p.risk_score}</td>
                      <td style={td}>{pill(p.risk_level, levelKind(p.risk_level))}</td>
                      <td style={td}>{pill(p.review_status, reviewKind(p.review_status))}</td>
                      <td style={{ ...td, color: tokens.textDim, whiteSpace: "normal", maxWidth: 320 }}>
                        {p.last_event_type || "—"}
                        {p.last_reason ? <div style={{ fontSize: 11, color: tokens.textDim2 }}>{p.last_reason}</div> : null}
                      </td>
                      <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{fmtDateTime(p.last_event_on)}</td>
                      <td style={td}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" variant="outlined" color="inherit" startIcon={<FactCheckRoundedIcon />}
                            sx={{ textTransform: "none" }}
                            onClick={() => setConfirm({ player: p, mode: "review" })}>
                            Review
                          </Button>
                          {p.blocked ? (
                            <Button size="small" variant="outlined" startIcon={<LockOpenRoundedIcon />}
                              sx={{ textTransform: "none", color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}
                              onClick={() => setConfirm({ player: p, mode: "unblock" })}>
                              Unblock
                            </Button>
                          ) : (
                            <Button size="small" variant="outlined" startIcon={<BlockRoundedIcon />}
                              sx={{ textTransform: "none", color: tokens.danger, borderColor: "rgba(242,92,102,0.4)" }}
                              onClick={() => setConfirm({ player: p, mode: "block" })}>
                              Block
                            </Button>
                          )}
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <TablePagination
              component="div" count={playerTotal} page={playerPage} rowsPerPage={playerRows}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => setPlayerPage(p)}
              onRowsPerPageChange={(e) => { setPlayerRows(parseInt(e.target.value, 10)); setPlayerPage(0); }}
              ActionsComponent={TablePaginationActions}
            />
          </SectionCard>
        )}

        {tab === 1 && (
          <SectionCard
            title="Risk events"
            subtitle="Every rule the engine matched. A blocked bet is one row here; ordinary play writes nothing."
            action={
              <TextField select size="small" label="Decision" value={decisionFilter} sx={filterFieldSx}
                onChange={(e) => { setDecisionFilter(e.target.value); setEventPage(0); }}>
                {DECISION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            }
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>When</th>
                    <th style={th}>Player</th>
                    <th style={th}>Rule</th>
                    <th style={th}>Decision</th>
                    <th style={th}>Points</th>
                    <th style={th}>Stake</th>
                    <th style={th}>Reason</th>
                    <th style={th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr><td style={{ ...td, color: tokens.textDim }} colSpan={8}>No risk events recorded.</td></tr>
                  )}
                  {events.map((e) => (
                    <tr key={e.id_risk_event}>
                      <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{fmtDateTime(e.created_on)}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{e.username || `#${e.id_user}`}</div>
                        <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>{e.id_user}</div>
                      </td>
                      <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{e.rule_code || e.event_type}</td>
                      <td style={td}>{pill(e.decision, decisionKind(e.decision))}</td>
                      <td style={{ ...td, fontFamily: mono }}>{e.risk_points}</td>
                      <td style={{ ...td, fontFamily: mono }}>{e.amount == null ? "—" : e.amount}</td>
                      <td style={{ ...td, color: tokens.textDim, whiteSpace: "normal", maxWidth: 340 }}>{e.reason || "—"}</td>
                      <td style={{ ...td, fontFamily: mono, fontSize: 11 }}>{e.ip_address || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <TablePagination
              component="div" count={eventTotal} page={eventPage} rowsPerPage={eventRows}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => setEventPage(p)}
              onRowsPerPageChange={(ev) => { setEventRows(parseInt(ev.target.value, 10)); setEventPage(0); }}
              ActionsComponent={TablePaginationActions}
            />
          </SectionCard>
        )}

        {tab === 2 && (
          <SectionCard
            title="Rule configuration"
            subtitle="Changes take effect within a minute across both APIs and are recorded in the risk audit log."
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Rule</th>
                    <th style={th}>Type</th>
                    <th style={th}>Threshold</th>
                    <th style={th}>Points</th>
                    <th style={th}>Action</th>
                    <th style={th}>Enabled</th>
                    <th style={th}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.rule_code}>
                      <td style={{ ...td, fontFamily: mono, fontSize: 11.5, fontWeight: 600 }}>{r.rule_code}</td>
                      <td style={td}>{pill(r.rule_type, r.rule_type === "LIMIT" ? "warn" : "neutral")}</td>
                      <td style={td}>
                        <Tooltip title="Empty means the player's own configured limit is used">
                          <TextField
                            size="small" defaultValue={r.threshold ?? ""} placeholder="player limit"
                            sx={{ width: 110, "& .MuiInputBase-root": { fontSize: 12.5, fontFamily: mono } }}
                            onBlur={(e) => {
                              const next = e.target.value.trim();
                              if (next !== String(r.threshold ?? "")) changeRuleThreshold(r, next);
                            }}
                          />
                        </Tooltip>
                      </td>
                      <td style={{ ...td, fontFamily: mono }}>{r.risk_points}</td>
                      <td style={td}>
                        <TextField select size="small" value={r.action || "REVIEW"}
                          sx={{ width: 120, "& .MuiInputBase-root": { fontSize: 12.5 } }}
                          onChange={(e) => changeRuleAction(r, e.target.value)}>
                          {ACTION_OPTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                        </TextField>
                      </td>
                      <td style={td}>
                        <Switch size="small" checked={Boolean(r.enabled)}
                          onChange={(e) => toggleRule(r, e.target.checked)} />
                      </td>
                      <td style={{ ...td, color: tokens.textDim, whiteSpace: "normal", maxWidth: 380 }}>{r.description}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1.5, mt: 1.75, borderRadius: "10px", borderLeft: `3px solid ${tokens.danger}`, border: `1px solid ${tokens.border}`, background: tokens.cardBg }}>
              <Typography sx={{ color: tokens.danger }}>⚑</Typography>
              <Typography variant="caption" color="text.secondary">
                Disabling a LIMIT rule stops a responsible-gaming control from being enforced on live bets. Every change here is written to the risk audit log against your account.
              </Typography>
            </Box>
          </SectionCard>
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={
          confirm?.mode === "block" ? "Block player" :
          confirm?.mode === "unblock" ? "Unblock player" : "Mark as reviewed"
        }
        facts={confirm ? [
          ["Player", confirm.player.username || `#${confirm.player.id_user}`],
          ["User id", String(confirm.player.id_user)],
          ["Risk score", `${confirm.player.risk_score} (${confirm.player.risk_level})`],
          ["Last signal", confirm.player.last_event_type || "—"],
        ] : null}
        question={
          confirm?.mode === "block" ? "Block this player from placing any further bets?" :
          confirm?.mode === "unblock" ? "Allow this player to bet again?" :
          "Mark this player as reviewed and cleared?"
        }
        note={
          confirm?.mode === "block"
            ? "Every bet is rejected until the block is lifted. Self-exclusion and cool-off are separate and are not changed by this."
            : "Recorded in the risk audit log against your admin account."
        }
        actionLabel={confirm?.mode === "block" ? "Block" : confirm?.mode === "unblock" ? "Unblock" : "Clear"}
        actionColor={confirm?.mode === "block" ? tokens.danger : tokens.secondary}
        onAction={() => {
          if (!confirm) return;
          if (confirm.mode === "block") submitReview(confirm.player, null, true, "Blocked from risk dashboard");
          else if (confirm.mode === "unblock") submitReview(confirm.player, null, false, "Unblocked from risk dashboard");
          else submitReview(confirm.player, "CLEARED", null, "Reviewed from risk dashboard");
        }}
      />
    </Box>
  );
}
